import { Club } from '@/staff/domain/club/Club';
import { Language } from '@/common/domain/Language';
import { ClubToUpdate } from '@/staff/domain/club/ClubToUpdate';
import { Optional } from '@/common/domain/Optional';
import { emptyTranslationsUi, fromTranslations, toUpdatedTranslations, TranslationUi } from '@/common/primary/Translation.ui';
import { Email } from '@/staff/domain/Email';
import {
  ClubTaxDeductionFormUi,
  emptyClubTaxDeductionForm,
  fromClubTaxDeduction,
  toClubTaxDeductionToUpdate,
} from '@/staff/primary/club/club-form/ClubTaxDeductionForm.ui';
import { isClubTaxDeductionToUpdateEmpty } from '@/staff/domain/club/ClubTaxDeductionToUpdate';
import { ClubModel } from '@/staff/domain/club/ClubModel';

export interface ClubFormUi {
  name: string;
  platformName: string;
  logoUrl: string;
  bannerUrl: string;
  contactEmail: string;
  taxDeduction: ClubTaxDeductionFormUi;
  recipientEmails: string[];
  presentationTranslations: TranslationUi[];
  isFoundation: boolean;
}

export const fromClub = (club: Club, recipientEmails: Email[], languages: Language[]): ClubFormUi => ({
  name: club.name,
  platformName: club.platformName,
  logoUrl: club.logoUrl,
  bannerUrl: club.bannerUrl,
  contactEmail: club.contactEmail,
  taxDeduction: fromClubTaxDeduction(club.taxDeduction, languages),
  recipientEmails: recipientEmails,
  presentationTranslations: fromTranslations(languages, club.presentationTranslations!),
  isFoundation: club.model === ClubModel.FOUNDATION,
});

const toOptionalString = (formValue: string, domainValue: string): Optional<string> =>
  Optional.ofEmpty(formValue).filter(str => str.localeCompare(domainValue) !== 0);

const toOptionalArrayString = (formValue: string[], domainValue: string[]): Optional<string[]> =>
  Optional.ofUndefinable(formValue).filter(strArray => JSON.stringify(strArray) !== JSON.stringify(domainValue));

export const toClubToUpdate = (clubForm: ClubFormUi, club: Club, recipientEmails: Email[]): ClubToUpdate => ({
  slug: club.slug,
  name: toOptionalString(clubForm.name, club.name),
  platformName: toOptionalString(clubForm.platformName, club.platformName),
  logoUrl: toOptionalString(clubForm.logoUrl, club.logoUrl),
  bannerUrl: toOptionalString(clubForm.bannerUrl, club.bannerUrl),
  contactEmail: toOptionalString(clubForm.contactEmail, club.contactEmail),
  taxDeduction: toOptionalClubTaxDeductionToUpdate(clubForm, club),
  recipientEmails: toOptionalArrayString(clubForm.recipientEmails, recipientEmails),
  presentationTranslations: toUpdatedTranslations(club.presentationTranslations!, clubForm.presentationTranslations),
});

const toOptionalClubTaxDeductionToUpdate = (clubForm: ClubFormUi, club: Club) =>
  Optional.of(toClubTaxDeductionToUpdate(clubForm.taxDeduction, club.taxDeduction)).filter(
    update => !isClubTaxDeductionToUpdateEmpty(update)
  );

export const emptyClubForm = (languages: string[]): ClubFormUi => ({
  name: '',
  platformName: '',
  logoUrl: '',
  bannerUrl: '',
  contactEmail: '',
  recipientEmails: [''],
  taxDeduction: emptyClubTaxDeductionForm(languages),
  presentationTranslations: emptyTranslationsUi(languages),
  isFoundation: false,
});

export const hasClubFormChanged = (clubForm: ClubFormUi, comparedClub: ClubFormUi) =>
  JSON.stringify(clubForm) !== JSON.stringify(comparedClub);

export type ClubTextFormInput = keyof Pick<ClubFormUi, 'name' | 'platformName' | 'logoUrl' | 'bannerUrl'>;
